import { HomeSearch } from './homesearch';
import { TProductCategory, TStore } from '@components/types';
import { getCityStaysPath, getSafeUrl } from '@core/utils';
import { ArrowRight, BedDouble, ShieldCheck, Store } from 'lucide-react';
import Link from 'next/link';
import React, { useMemo } from 'react';

const PREMIUM = '#1F3A5C';
const BRAND = '#ED7829';

type Props = {
    stays: TStore[];
    categories: TProductCategory[];
};

// Top of the home page: hotel booking first (search + popular destinations), local stores second.
export const HomeHero: React.FC<Props> = ({ stays, categories }) => {
    // Destinations come from where the hotels actually are, most hotels first.
    const destinations = useMemo(() => {
        const byId = new Map<number, { id: number; name: string; count: number }>();
        (stays || []).forEach((s) => {
            const id = s.address?.city_id;
            const name = s.address?.city?.name;
            if (!(id > 0) || !name) return;
            const hit = byId.get(id);
            if (hit) hit.count += 1; else byId.set(id, { id, name, count: 1 });
        });
        return Array.from(byId.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)).slice(0, 8);
    }, [stays]);

    // Shortcuts for local shopping: food and grocery categories first, then the rest by API order.
    const storeShortcuts = useMemo(() => {
        const local = /food|grocery|restaurant|sweet|bakery|pharmacy|medicine|vegetable|fruit/i;
        const list = categories || [];
        return [...list.filter((c) => local.test(c.name)), ...list.filter((c) => !local.test(c.name))].slice(0, 5);
    }, [categories]);

    return (
        <section aria-label="Book hotels and explore local stores" className="px-2 relative z-20">
            {/* No overflow-hidden on this card: the search dropdown must be able to extend past its edge.
                The decorative glow is clipped by its own wrapper instead. */}
            <div className="relative rounded-3xl text-white"
                style={{ background: `linear-gradient(135deg, ${PREMIUM} 0%, #16293f 55%, #0f1c2e 100%)` }}>
                <div aria-hidden="true" className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
                    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: BRAND }} />
                </div>

                <div className="relative box-content grid grid-cols-1 lg:grid-cols-5 gap-6 p-5 md:p-8">
                    {/* ── 1. Hotels (primary) ── */}
                    <div className="lg:col-span-3 min-w-0 flex flex-col gap-4 relative z-10">
                        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold text-white/85">
                            <BedDouble className="w-3.5 h-3.5" /> Hotels &amp; stays
                        </span>
                        <h1 className="text-2xl md:text-4xl font-bold tracking-tight font-manrope m-0">
                            Book your stay at the <span style={{ color: '#ffb27a' }}>best rates</span>
                        </h1>
                        <p className="text-white/70 text-sm md:text-base max-w-xl m-0">
                            Hotels, resorts and homestays across India with live room availability and clear, tax-inclusive prices.
                        </p>

                        <HomeSearch className="mt-1" />

                        {destinations.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-white/50">Popular destinations</span>
                                <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
                                    {destinations.map((d) => (
                                        <li key={d.id}>
                                            <Link href={getCityStaysPath(d.name, d.id)} prefetch={false}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 px-3 py-1.5 text-xs font-semibold no-underline transition-colors"
                                                style={{ color: '#fff' }}>
                                                {d.name}
                                                <span className="text-white/50 font-medium">{d.count}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/60">
                            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Verified partner properties</span>
                            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> No hidden fees</span>
                        </div>
                    </div>

                    {/* ── 2. Local stores ── */}
                    <div className="lg:col-span-2 flex flex-col gap-4 rounded-2xl bg-white text-neutral-900 p-5 md:p-6 shadow-lg">
                        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-orange-50 border border-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                            <Store className="w-3.5 h-3.5" /> Local stores
                        </span>
                        <h2 className="text-xl md:text-2xl font-bold font-manrope m-0">Explore stores near you</h2>
                        <p className="text-neutral-500 text-sm m-0">
                            Order food, groceries and everyday essentials from shops and restaurants around you.
                        </p>

                        {storeShortcuts.length > 0 && (
                            <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
                                {storeShortcuts.map((c) => (
                                    <li key={c.id}>
                                        <Link href={`/category/${c.id}/${getSafeUrl(c.name)}`} prefetch={false}
                                            className="inline-flex rounded-full border border-solid border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 px-3 py-1.5 text-xs font-semibold no-underline transition-colors"
                                            style={{ color: '#404040' }}>
                                            {c.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <Link href="/stores" prefetch={false}
                            className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold no-underline hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: BRAND, color: '#fff' }}>
                            Explore local stores <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};
